import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { imageSrc } from 'apps/file/utils';

import { ComponentRegistry } from 'core/registry';

import {
  SvgIcon,
  Heading,
  Tooltip,
  Button,
  Box,
  Avatar,
  Spinner,
} from 'components';
import { Close, Clock } from 'icons';
import Caret from 'components/Select/caret';

import { FormField } from 'libs/forms/styles';
import { Dropdown } from 'apps/autocomplete/views/dropdown';

import {
  UserSettingsWrapper,
  HeaderWrapper,
  SearchWrapper,
  ParticipantsWrapper,
  FooterWrapper,
  RemoveIcon,
  NativeSelectWrapper,
  NativeSelect,
  CaretWrapper,
} from 'libs/rendering/components/header/userSettings.styled';

const prettifiedGroupName = {
  owner: 'File owner',
  admin: 'Admin',
  collaborator: 'Collaborate',
  'limited-collaborator': 'View Only',
};

const permissionTooltip = {
  admin: '(View, Comment, Download, Upload Revisions and Manage Collaborators)',
  collaborator: '(View, Comment, Download and Upload Revisions)',
  'limited-collaborator': '(View and Comment)',
};

const UserSettings = observer(props => {
  const { sharedFileManager, closeMenu } = props;
  const { sharedFile } = sharedFileManager;

  if (!sharedFile) return null;

  const limitedCollaboratorGroup = sharedFile.groups.find(
    group => group.name === 'limited-collaborator',
  );
  const collaboratorGroup = sharedFile.groups.find(
    group => group.name === 'collaborator',
  );
  const adminGroup = sharedFile.groups.find(group => group.name === 'admin');

  if (!(collaboratorGroup && limitedCollaboratorGroup)) return null;

  const fieldStore =
    limitedCollaboratorGroup?.membersForm.fieldStores.get('newMembers');
  const ListField = ComponentRegistry.get('forms.ListField');
  const refs = {};

  const setInputRef = input => {
    refs.newMembers = input;
  };

  const handleSendInviteBtnClick = () => {
    limitedCollaboratorGroup.InviteMembers();
  };

  const { participants } = sharedFile.groups;

  const handleSelectChange = participant => event => {
    sharedFile.groups.MoveParticipant(participant, event.target.value);
  };

  const participantList = participants.map(participant => {
    const { profile, group, invite } = participant;
    let optionList = null;
    let removeBtn = null;

    if (!profile) return null;

    const isFileOwner = sharedFile.creator.username === profile.username;

    if (isFileOwner) {
      optionList = <option>{prettifiedGroupName.owner}</option>;
    } else if (sharedFile.canUpdate) {
      optionList = (
        <>
          <option value={limitedCollaboratorGroup.id}>
            {prettifiedGroupName[limitedCollaboratorGroup.name]}
          </option>
          <option value={collaboratorGroup.id}>
            {prettifiedGroupName[collaboratorGroup.name]}
          </option>
          <option value={adminGroup.id}>
            {prettifiedGroupName[adminGroup.name]}
          </option>
        </>
      );
      // Only admin (canUpdate property) can remove themselves from the file
      removeBtn = (
        <Box
          data-for='permissionsTooltip'
          data-tip='Remove collaborator'
          ml={6}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {group.isLoading[invite?.id || profile.username] ? (
            <Spinner size='small' />
          ) : (
            <RemoveIcon
              name='close'
              size='xsmall'
              onClick={e => {
                e.preventDefault();
                if (invite) {
                  group.RemoveInvite(invite.id);
                } else {
                  group.RemoveMember(profile.username);
                }
              }}
            />
          )}
        </Box>
      );
    } else {
      optionList = (
        <option value={group.id}>{prettifiedGroupName[group.name]}</option>
      );
    }

    return (
      <Box
        data-test-id={`collaborator-row-${profile.username}`}
        key={profile.username}
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
        mb={4}
      >
        <Avatar
          src={imageSrc(profile.avatar, profile.imageFallbackPath)}
          size='medium'
        />
        <Box
          ml={2}
          sx={{
            flexDirection: 'column',
            display: 'flex',
            width: 164,
          }}
        >
          <Heading size='xsmall'>{profile.fullName}</Heading>
          <Heading size='xsmall' variant='minimal'>
            {profile.username || participant.displayName}
          </Heading>
        </Box>
        <NativeSelectWrapper>
          <CaretWrapper>
            <Caret show={false} size='xsmall' />
          </CaretWrapper>
          <NativeSelect
            value={participant.group.id}
            onChange={handleSelectChange(participant)}
            data-for='permissionsTooltip'
            data-tip={permissionTooltip[participant.group.name]}
          >
            {optionList}
          </NativeSelect>
        </NativeSelectWrapper>
        {removeBtn}
        {invite && invite.status === 'PENDING' && (
          <Box
            ml={8}
            sx={{ display: 'inline-flex' }}
            data-tip='Pending confirmation'
            data-for='permissionsTooltip'
          >
            <SvgIcon icon={<Clock />} />
          </Box>
        )}
      </Box>
    );
  });

  return (
    <>
      <Tooltip delayShow={300} place='bottom' id='permissionsTooltip' />
      <UserSettingsWrapper>
        {sharedFile.canUpdate && (
          <HeaderWrapper>
            <Heading level='4' size='default'>
              Invite Collaborators
            </Heading>
            <SvgIcon icon={<Close />} size='medium' onClick={closeMenu} />
          </HeaderWrapper>
        )}
        {sharedFile.canUpdate && (
          <SearchWrapper>
            <FormField>
              <ListField
                ref={setInputRef}
                iconName='search'
                fieldStore={fieldStore}
                noValidationIcon
                responsive
              />
              <Dropdown
                inputFields={refs}
                fieldName='newMembers'
                manager={fieldStore.optionsManager}
              />
            </FormField>
            <Button
              text='Send invite'
              data-test-id='collabortors-send-invite'
              size='small'
              loading={limitedCollaboratorGroup.isLoading.inviteMembers}
              onClick={handleSendInviteBtnClick}
              outline
            />
          </SearchWrapper>
        )}
        <ParticipantsWrapper>
          {sharedFile.canUpdate ? (
            <Heading level='4' size='default' margin='0.5rem 1rem'>
              Collaborator Permissions
            </Heading>
          ) : (
            <HeaderWrapper>
              <Heading level='4' size='default'>
                Collaborators
              </Heading>
              <SvgIcon icon={<Close />} size='medium' onClick={closeMenu} />
            </HeaderWrapper>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {participantList}
          </Box>
        </ParticipantsWrapper>
        {sharedFile.canUpdate && (
          <FooterWrapper>
            <Button
              text={<>Copy invite link</>}
              size='small'
              appearance='plain'
              variant='tertiary'
              icon='link'
              iconPosition='left'
              onClick={sharedFileManager.CopyMagicLink}
              data-test-id='copy-invite-link-button'
            />
          </FooterWrapper>
        )}
      </UserSettingsWrapper>
    </>
  );
});

UserSettings.propTypes = {
  sharedFileManager: PropTypes.object,
  closeMenu: PropTypes.func,
};

export default UserSettings;
